/**
 * iter318 wave 10 — Parent Request Timeline.
 *
 * Renders the parent's pause/resume/cancel requests as a compact
 * chronological timeline. Each row shows type + status + requested
 * date + admin decision (if any). Empty state hides itself so the
 * Enrollments tab stays clean when there's no request history.
 *
 * Data source: GET /api/enrollments/requests/my-requests
 * (already fetched in the parent Enrollments page).
 */
import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  PauseCircle, PlayCircle, XCircle, Clock, CheckCircle2, AlertCircle,
  Send, CalendarCheck,
} from 'lucide-react';

const _fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
    });
  } catch {
    return '';
  }
};

const _fmtTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const TYPE_META = {
  pause: {
    label: 'Pause',
    icon: PauseCircle,
    color: 'text-amber-600 bg-amber-50 border-amber-200',
  },
  resume: {
    label: 'Resume',
    icon: PlayCircle,
    color: 'text-emerald-600 bg-emerald-50 border-emerald-200',
  },
  cancel: {
    label: 'Cancel',
    icon: XCircle,
    color: 'text-rose-600 bg-rose-50 border-rose-200',
  },
};

const STATUS_META = {
  pending: {
    label: 'Awaiting approval',
    icon: Clock,
    color: 'text-amber-700 bg-amber-100',
  },
  approved: {
    label: 'Approved',
    icon: CheckCircle2,
    color: 'text-emerald-700 bg-emerald-100',
  },
  rejected: {
    label: 'Not approved',
    icon: AlertCircle,
    color: 'text-rose-700 bg-rose-100',
  },
};

/**
 * Build the milestone list for a single request. iter318 wave 11 —
 * enrichment: expand every request into an ordered mini-timeline so
 * parents see the full decision journey, not just the outcome badge.
 *
 * Milestones are inferred from the fields the backend stamps on
 * `enrollment_requests` / `subscription_requests`:
 *   • submitted        — always present (created_at / requested_at)
 *   • reviewed         — reviewed_at OR approved_at OR rejected_at
 *   • applied/rejected — the terminal state; for a cancel request
 *                        this also lands on effective_date
 */
function buildMilestones(r) {
  const submittedAt = r.created_at || r.requested_at;
  const reviewedAt = r.reviewed_at || r.approved_at || r.rejected_at;
  const effectiveAt = r.effective_date;
  const isTerminal = r.status === 'approved' || r.status === 'rejected';

  const milestones = [
    {
      key: 'submitted',
      label: 'Submitted',
      at: submittedAt,
      state: 'done',
      icon: Send,
    },
    {
      key: 'reviewed',
      label:
        r.status === 'approved'
          ? 'Approved by admin'
          : r.status === 'rejected'
          ? 'Rejected by admin'
          : 'Under review',
      at: reviewedAt,
      state: reviewedAt ? 'done' : 'active',
      icon: isTerminal
        ? r.status === 'approved'
          ? CheckCircle2
          : AlertCircle
        : Clock,
    },
  ];

  // Third step differs by request type
  if (isTerminal) {
    if (r.type === 'cancel') {
      milestones.push({
        key: 'effective',
        label: 'Effective date',
        at: effectiveAt,
        state: effectiveAt && new Date(effectiveAt) <= new Date() ? 'done' : 'upcoming',
        icon: CalendarCheck,
      });
    } else if (r.type === 'pause') {
      milestones.push({
        key: 'applied',
        label: r.status === 'approved' ? 'Pause applied' : 'Not applied',
        at: reviewedAt,
        state: r.status === 'approved' ? 'done' : 'skipped',
        icon: r.status === 'approved' ? PauseCircle : AlertCircle,
      });
    } else if (r.type === 'resume') {
      milestones.push({
        key: 'applied',
        label: r.status === 'approved' ? 'Resumed' : 'Not applied',
        at: reviewedAt,
        state: r.status === 'approved' ? 'done' : 'skipped',
        icon: r.status === 'approved' ? PlayCircle : AlertCircle,
      });
    }
  } else {
    // Pending — show the eventual step greyed out.
    milestones.push({
      key: 'applied',
      label:
        r.type === 'cancel'
          ? 'Will take effect'
          : r.type === 'pause'
          ? 'Pause will apply'
          : 'Enrollment will resume',
      at: r.type === 'cancel' ? effectiveAt : null,
      state: 'upcoming',
      icon: TYPE_META[r.type]?.icon || Clock,
    });
  }
  return milestones;
}

export default function ParentRequestTimeline({ requests = [], enrollmentById = {} }) {
  const sorted = useMemo(() => {
    return [...requests].sort((a, b) => {
      const ta = new Date(a.created_at || a.requested_at || 0).getTime();
      const tb = new Date(b.created_at || b.requested_at || 0).getTime();
      return tb - ta;   // newest first
    });
  }, [requests]);

  if (!sorted.length) return null;

  return (
    <Card className="mb-4" data-testid="parent-request-timeline">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="h-4 w-4 text-slate-500" />
          Recent Requests
          <Badge variant="outline" className="ml-1 text-xs">
            {sorted.length}
          </Badge>
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Track the status of your pause, resume, and cancel requests.
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        <ol className="relative border-l border-slate-200 ml-2">
          {sorted.map((r) => {
            const type = TYPE_META[r.type] || TYPE_META.pause;
            const status = STATUS_META[r.status] || STATUS_META.pending;
            const TypeIcon = type.icon;
            const StatusIcon = status.icon;
            const enroll = enrollmentById[r.enrollment_id];
            const label =
              r.program_name ||
              enroll?.program_name ||
              r.player_name ||
              enroll?.player_name ||
              'Enrollment';
            const decidedAt = r.reviewed_at || r.approved_at || r.rejected_at;
            return (
              <li
                key={r.id}
                className="mb-3 ml-4"
                data-testid={`request-timeline-row-${r.id}`}
              >
                <span
                  className={`absolute -left-2 flex h-4 w-4 items-center justify-center rounded-full border-2 border-white ${type.color}`}
                  aria-hidden
                >
                  <TypeIcon className="h-3 w-3" />
                </span>
                <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {type.label} request · {label}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        Submitted {_fmtDate(r.created_at || r.requested_at)}
                        <span className="text-slate-300 mx-1.5">·</span>
                        {_fmtTime(r.created_at || r.requested_at)}
                        {r.pause_weeks && (
                          <>
                            <span className="text-slate-300 mx-1.5">·</span>
                            {r.pause_weeks}-week pause
                          </>
                        )}
                        {r.effective_date && (
                          <>
                            <span className="text-slate-300 mx-1.5">·</span>
                            Effective {_fmtDate(r.effective_date)}
                          </>
                        )}
                      </div>
                      {r.reason && (
                        <div className="text-xs text-slate-600 mt-1 italic">
                          &ldquo;{r.reason}&rdquo;
                        </div>
                      )}
                      {r.admin_notes && (
                        <div className="text-xs text-slate-700 mt-1">
                          <span className="font-medium">Admin:</span> {r.admin_notes}
                        </div>
                      )}
                      {/* iter318 wave 11 — Mini-timeline of the
                          decision journey. Submitted → Reviewed →
                          Applied/Effective. Greyed-out for upcoming
                          steps; strike-through for skipped
                          (rejected). Gives the parent a real sense
                          of progress rather than a single outcome
                          badge. */}
                      <ol
                        className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px]"
                        data-testid={`request-mini-timeline-${r.id}`}
                      >
                        {buildMilestones(r).map((m, idx) => {
                          const MIcon = m.icon;
                          const dotColor =
                            m.state === 'done'
                              ? 'text-emerald-600 bg-emerald-50'
                              : m.state === 'active'
                              ? 'text-amber-600 bg-amber-50 animate-pulse'
                              : m.state === 'skipped'
                              ? 'text-slate-400 bg-slate-100 line-through'
                              : 'text-slate-400 bg-slate-100';
                          const textColor =
                            m.state === 'done'
                              ? 'text-slate-700'
                              : m.state === 'active'
                              ? 'text-slate-900 font-medium'
                              : m.state === 'skipped'
                              ? 'text-slate-400 line-through'
                              : 'text-slate-400';
                          return (
                            <li
                              key={m.key}
                              className="flex items-center gap-1.5"
                              data-testid={`milestone-${r.id}-${m.key}`}
                            >
                              {idx > 0 && (
                                <span className="text-slate-300" aria-hidden>→</span>
                              )}
                              <span
                                className={`inline-flex items-center justify-center h-4 w-4 rounded-full ${dotColor}`}
                                aria-hidden
                              >
                                <MIcon className="h-2.5 w-2.5" />
                              </span>
                              <span className={textColor}>
                                {m.label}
                                {m.at && m.state === 'done' && (
                                  <span className="text-slate-400 ml-1">
                                    · {_fmtDate(m.at)}
                                  </span>
                                )}
                                {m.at && m.state === 'upcoming' && (
                                  <span className="text-slate-400 ml-1">
                                    · {_fmtDate(m.at)}
                                  </span>
                                )}
                              </span>
                            </li>
                          );
                        })}
                      </ol>
                    </div>
                    <Badge
                      variant="outline"
                      className={`text-xs ${status.color} border-0 whitespace-nowrap`}
                      data-testid={`request-timeline-status-${r.id}`}
                    >
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {status.label}
                      {decidedAt && (r.status === 'approved' || r.status === 'rejected') && (
                        <span className="ml-1 opacity-75">· {_fmtDate(decidedAt)}</span>
                      )}
                    </Badge>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}
